import Vue from 'vue';

import type { CheckItem, FeedbackItem } from '@/types';

interface CustomVueEvent<Args extends any[]> {
  emit(...args: Args): void;
  on(listener: (...args: Args) => void): () => void;
}

interface CustomVueEvents {
  logout: CustomVueEvent<[
    addTask: (task: Promise<unknown>) => void,
  ]>;
  feedbackResolve: CustomVueEvent<[
    check: CheckItem,
    feedback: FeedbackItem,
  ]>;
  feedbackSelection: CustomVueEvent<[
    check: CheckItem | undefined,
    selected: boolean
  ]>,
  feedbackCardToggle: CustomVueEvent<[
    // The check item that groups the feedbacks.
    check: CheckItem,
    // The feedback item from the group, that just has been toggled.
    feedback: FeedbackItem,
    // The state of whether the feedback card is opened or collapsed.
    collapsed: boolean,
    // The state of whether all cards from the same group are collapsed.
    groupCollapsed: boolean,
  ]>,
  feedbackSuggestionApply: CustomVueEvent<[
    item: FeedbackItem,
    replaceIndex: number,
    newText: string,
    oldText: string,
  ]>,
  feedbackSuggestionMouseenter: CustomVueEvent<[
    item: FeedbackItem,
    replaceIndex: number,
  ]>,
  feedbackSuggestionMouseleave: CustomVueEvent<[
    item: FeedbackItem,
    replaceIndex: number,
  ]>,
  editorScrollToParagraph: CustomVueEvent<[
    paragraphIndex: number,
  ]>,
}

interface EventsPool {
  on(name: keyof CustomVueEvents, ...args: Parameters<CustomVueEvents[typeof name]['on']>): this;
  destroy(): void;
}

interface SubscribersPool {
  $poolFor(component: Vue): EventsPool;
}

declare module 'vue/types/vue' {
  export interface Vue {
    $eventsBus: CustomVueEvents & SubscribersPool;
  }
}

const subscribersPools = new WeakMap<Vue, EventsPool>();
const eventsBus: Vue & CustomVueEvents & SubscribersPool = new Proxy(new Vue(), {
  get(target, property) {
    // @ts-ignore
    return property in target ? target[property] : createCustomEvent(property);
  },
});

eventsBus.$poolFor = (component) => {
  const existingPool = subscribersPools.get(component);

  if (existingPool) {
    return existingPool;
  }

  const subscriptions: Function[] = [];
  const newPool: EventsPool = {
    on(name, ...args) {
      // @ts-ignore
      subscriptions.push(eventsBus[name].on(...args));
      return this;
    },
    destroy() {
      subscribersPools.delete(component);
      subscriptions.forEach((unsubscribe) => unsubscribe());
      subscriptions.length = 0;
    },
  };

  subscribersPools.set(component, newPool);

  return newPool;
};

function createCustomEvent(name: keyof CustomVueEvents): CustomVueEvents[typeof name] {
  return eventsBus[name] = {
    emit(...args) {
      eventsBus.$emit(name, ...args);
    },
    on(listener) {
      eventsBus.$on(name, listener);

      return () => {
        eventsBus.$off(name, listener);
      };
    },
  };
}

Vue.prototype.$eventsBus = eventsBus;

export { eventsBus };
